import { db, store } from "../..";
import { getStateFor, resolveOfflineThresholds } from "../scoutState";
import { Scout, ScoutState } from "../model/scout";
import { removeScoutAction, setScoutAction } from "../organisationReducer";

export const listenForScouts = (organisationId: string) => {
  return db
    .doc("commander")
    .collection("organisations")
    .doc(organisationId)
    .collection("scouts")
    .onSnapshot((snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const scout: Scout = docToScout(change.doc);
        if (change.type === "added" || change.type === "modified") {
          // console.log(JSON.stringify(change.doc.data()));

          if (scout.status === undefined) {
            // Ignoring scouts without status
            return;
          }

          store.dispatch(setScoutAction(scout));
        }

        if (change.type === "removed") {
          store.dispatch(removeScoutAction(scout));
        }
      });
    });
};

export const queryScouts = (
  organisationId: string,
  callback: (_: Scout[]) => void,
) => {
  return db
    .doc("commander")
    .collection("organisations")
    .doc(organisationId)
    .collection("scouts")
    .get()
    .then((snapshot) => {
      let result: Scout[] = [];
      snapshot.docs.forEach((doc) => {
        // @ts-ignore
        const scout: Scout = docToScout(doc);
        if (scout.state !== ScoutState.OfflineIgnore) {
          result.push(scout);
        }
      });
      callback(result);
    });
};

/**
 * One-time fetch of every scout's display name for an organisation, keyed by deviceId.
 * Unlike queryScouts this keeps OfflineIgnore scouts, since old incidents are often
 * from devices that have long since gone quiet.
 */
export const queryScoutDisplayNames = (
  organisationId: string,
): Promise<{ [deviceId: string]: string }> => {
  return db
    .doc("commander")
    .collection("organisations")
    .doc(organisationId)
    .collection("scouts")
    .get()
    .then(({ docs }) => {
      const displayNames: { [deviceId: string]: string } = {};
      docs.forEach((doc) => {
        const displayName = (doc.data() as Partial<Scout>)?.status?.displayName;
        if (displayName) {
          displayNames[doc.id] = displayName;
        }
      });
      return displayNames;
    });
};

export const listenForClockAnalytics = (
  deviceId: string,
  callback: (measurement: { [type: string]: number }) => void,
) => {
  return db
    .doc("analytics")
    .collection("scouts")
    .doc(deviceId)
    .collection("clock")
    .orderBy("hostTime", "desc")
    .limit(1)
    .onSnapshot((snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added" || change.type === "modified") {
          let data = change.doc.data();
          delete data["sessionStart"];
          callback(data);
        }
      });
    });
};

export const queryForClockAnalytics = (
  deviceId: string,
  fromTut: number, // from timestamp in mega Tut
  callback: (measurement: { [type: string]: number }[]) => void,
) => {
  let result = [];
  return db
    .doc("analytics")
    .collection("scouts")
    .doc(deviceId)
    .collection("clock")
    .where(
      "NETWORK",
      ">",
      fromTut
    )
    .orderBy("NETWORK", "asc")
    .get()
    .then((snapshot) => {
      snapshot.docs.forEach((doc) => {
        let data = doc.data()
        delete data["sessionStart"];
        // @ts-ignore
        result.push(data);
      });
      callback(result);
    });
};
export const getLastMeasurementForDevice = (
  deviceId: string,
): Promise<{ [_: string]: number } | undefined> => {
  return db
    .doc("analytics")
    .collection("scouts")
    .doc(deviceId)
    .collection("clock")
    .orderBy("NETWORK", "desc")
    .limit(1)
    .get()
    .then((snapshot) => {
      if (snapshot.size === 0) {
        return undefined;
      }
      return snapshot.docs[0].data() as { [_: string]: number };
    });
};

export const deleteClockAnalyticsMeasurements = (
  deviceId: string,
  finishedCallback: () => void,
) => {
  return db
    .doc("analytics")
    .collection("scouts")
    .doc(deviceId)
    .collection("clock")
    .get()
    .then((snapshot) => {
      snapshot.docs.forEach((doc) => {
        console.log("Deleting clock measurement", doc.id);
        db.doc("analytics")
          .collection("scouts")
          .doc(deviceId)
          .collection("clock")
          .doc(doc.id)
          .delete();
      });
      finishedCallback();
    });
};

export function docToScout(doc: any) {
  const data = doc.data() as Scout;
  // Stored state uses the organisation thresholds known right now; the pages re-derive with the resolved ones.
  const state = getStateFor(
    data,
    resolveOfflineThresholds(store.getState().organisation.organisationInfo?.offline, false)
  );

  const scout: Scout = {
    ...data,
    deviceId: doc.id,
    state,
  };
  return scout;
}
